/**
 * Presentation Library Search Agent
 * 
 * Sub-agent for searching across PowerPoint presentation libraries.
 * Implements the Agent-as-Tool pattern via AgentBaseClass.
 * 
 * ## Capabilities
 * - Semantic search (find by meaning using Voyage embeddings)
 * - Exact text search (find exact matches using FTS5)
 * - Fuzzy text search (find approximate matches)
 * - Visual search (find similar visuals using CLIP)
 * 
 * ## Result Types
 * - Slides: Full slide results
 * - Components: Cropped component regions (grouped shapes)
 * - Assets: Individual assets (tables, charts, images)
 * - Shapes: Individual shapes with text content
 * - Layouts: Master/layout templates
 * 
 * ## Storage
 * Results are stored in GlobalStateManager with composite keys using deck aliases:
 * - Slides: "d0|slide_257" (LLM-facing) → stored as "deck_abc123...|slide_257" (internal)
 * - Components: "component_abc12345" (globally unique, no deck context)
 * - Shapes: "d0|slide_257|shape_42"
 * - Layouts: "d0|layout_2147483951"
 * 
 * ## Usage
 * ```typescript
 * const result = await presentationLibrarySearchAgent.execute({
 *     requirements: [
 *         { id: 'A', requirement: 'Find slides with revenue charts' }
 *     ],
 *     documentId: 'CURRENT'  // or 0, 1, 2 for indexed decks
 * }, callbacks);
 * ```
 */

import { z } from "zod";
import { AgentBaseClass, type ToolPreflightContext, type ToolPreflightResult } from "../../base/agentBaseClass";
import { ToolDescription } from "../../base/toolDescription";
import type { SubmitResultsParams } from "../../base/submitResults.schema";
import {
    AgentResult,
    ResultItem,
    ResolvedContextItem,
    ThreadId,
    CancellationToken,
    AgentCallbacks,
} from "../../../core/types";
import { createCompositeId } from "../../../core/compositeIdUtils";
import { InPresentationSearchParamsSchema, type InPresentationSearchParams } from "./inPresentationSearch.schema";
import { SearchPresentationTool, type SearchPresentationResponse, type SearchResult } from "./tools";
import GlobalStateManager from "../../../globals/global-state/globalStateManager";
import type { ChatMessage } from "../../../globals/llm/baseAdapter";
import { getCurrentPresentationContext } from "../../../utils/presentationContext";
import { buildInPresentationSystemPrompt } from "./prompt";
import { getPresentationLibraryExclusions } from "../../../globals/global-state/searchHistory";

// ═══════════════════════════════════════════════════════════════════════════
// RESULT DATA TYPE
// ═══════════════════════════════════════════════════════════════════════════

/** Result data specific to presentation library search */
export interface PresentationLibraryResultData {
    /** Array of search result references */
    searchResults: ResolvedPresentationSearchResultItem[];
}

/** A resolved search result with full context */
export interface ResolvedPresentationSearchResultItem {
    /** Composite ID for context storage (e.g., "d0|slide_257" or "component_xyz123") */
    contextId: string;
    /** Result type: slide, component, asset, shape, layout */
    resultType: string;
    /** The result ID (slide_257, component_abc, etc.) */
    resultId: string;
    /** Deck/presentation ID */
    presentationId: string;
    /** Slide ID if applicable */
    slideId?: string;
    /** Slide index (0-based) */
    slideIndex?: number;
    /** Relevance score */
    score: number;
    /** Description of the result */
    description?: string;
    /** Text content if available */
    text?: string;
    /** Component SlideML payload if available */
    slideml?: Record<string, unknown>;
}

// ═══════════════════════════════════════════════════════════════════════════
// STORED RESULT DATA TYPE
// ═══════════════════════════════════════════════════════════════════════════

/** Data stored in GlobalStateManager for each search result */
interface StoredResultData {
    /** Composite ID */
    id: string;
    /** Result type */
    resultType: string;
    /** The actual result ID */
    resultId: string;
    /** Deck ID */
    presentationId: string;
    /** Slide ID if applicable */
    slideId?: string;
    /** Slide index */
    slideIndex?: number;
    /** Score */
    score: number;
    /** Description */
    description?: string;
    /** Text content */
    text?: string;
    /** Base64 image (not always included to save memory) */
    imageBase64?: string;
    /** Layout info */
    layoutId?: string;
    layoutName?: string;
    /** Shape info */
    shapeId?: string;
    shapeType?: string;
    /** Original metadata */
    metadata?: Record<string, unknown>;
    /** Component SlideML payload if available */
    slideml?: Record<string, unknown>;
    /** When stored */
    createdAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// PRESENTATION LIBRARY SEARCH AGENT
// ═══════════════════════════════════════════════════════════════════════════

// System prompt for agent-as-tool description (stored in Keywords AI)
const PRESENTATION_LIBRARY_SEARCH_AGENT_TOOL_PROMPT = `Search across PowerPoint presentations in the library.

Use this agent when you need to:
- Find examples of how content was built previously
- Search selected decks or all indexed presentations
- Resolve ambiguous instructions by finding analogous slides/components
- Retrieve reusable references (slides, components, layouts)

Returns references (slide_*, component_*) for token-efficient passing.`;

/**
 * Presentation Library Search Agent
 * 
 * Searches PowerPoint libraries for:
 * - Slides matching semantic or text queries
 * - Components (cropped regions)
 * - Individual shapes and assets
 * - Layouts/templates
 * - Visually similar content
 */
export class PresentationLibrarySearchAgent extends AgentBaseClass<InPresentationSearchParams, AgentResult<PresentationLibraryResultData>> {
    
    // ═══════════════════════════════════════════════════════════════════════
    // AGENT-AS-TOOL PROMPT CONFIGURATION
    // ═══════════════════════════════════════════════════════════════════════
    
    /** System prompt description for agent-as-tool */
    public readonly systemPromptDescription = PRESENTATION_LIBRARY_SEARCH_AGENT_TOOL_PROMPT;
    
    // ═══════════════════════════════════════════════════════════════════════
    // REQUIRED IMPLEMENTATIONS
    // ═══════════════════════════════════════════════════════════════════════
    
    readonly agentType = 'presentationLibrarySearch';
    readonly name = 'presentationLibrarySearchAgent';
    readonly isAgent = true; // Mark as agent-as-tool
    protected createsUndoCheckpoint = false; // Read-only search agent
    
    // Tools this agent can use
    private searchTool: SearchPresentationTool;
    protected toolCalls: SearchPresentationTool[];
    
    // ═══════════════════════════════════════════════════════════════════════
    // STREAMING FIELD CONFIGURATION (for Input Parameters UI)
    // ═══════════════════════════════════════════════════════════════════════
    
    /** Define fields to extract during streaming for progressive UI updates */
    fieldsToCheck = [
        { name: 'documentId', type: 'string' as const, list_status: false, order: 2 },
    ];
    
    /** Define list fields - items committed individually as they stream in */
    listsToCheck = [
        { 
            name: 'requirements', 
            type: 'array' as const, 
            item_type: 'object' as const,
            list_status: true, 
            order: 1,
            check_sub_field: 'requirement'  // Item ready when 'requirement' field exists
        },
        {
            name: 'documentIds',
            type: 'array' as const,
            item_type: 'string' as const,
            list_status: true,
            order: 3,
        },
    ];
    
    // Enable infrastructure for agent-as-tool pattern
    protected useThreadManagement = true;
    protected useTaskTracking = true;
    protected useContextResolution = true;
    
    // Enable streaming for real-time UI updates
    protected stream = true;
    
    // Allow longer responses for detailed search analysis
    // maxResponseTokens: inherits 0 from base → adapter uses model maximum
    
    // Track last search response for result processing
    private lastSearchResponse: SearchPresentationResponse | null = null;
    
    // Current deck context (uses parent's protected currentdocumentId)
    private deckRegistry: Map<number, { id: string; title: string; slidesReady: number; totalSlides: number }> = new Map();
    
    constructor() {
        super();
        
        // Initialize search tool
        this.searchTool = new SearchPresentationTool();
        this.toolCalls = [this.searchTool];
        
        this.description = new ToolDescription(
            this.name,
            this.systemPromptDescription,
            InPresentationSearchParamsSchema,
            {
                briefDescription: "Search across presentation libraries",
                whenToUse: [
                    "Finding slides by topic",
                    "Locating specific layouts",
                    "Finding visually similar slides",
                ],
                exampleUsage: '{"requirements": [{"id": "A", "requirement": "Find slides about revenue"}]}',
                commonMistakes: [
                    "Searching before slides are indexed",
                ],
                capabilities: [
                    "Semantic search",
                    "Exact text search",
                    "Visual similarity search",
                ],
            }
        );
        
        // Set paramsSchema for agent-as-tool pattern
        this.paramsSchema = InPresentationSearchParamsSchema as unknown as z.ZodType<InPresentationSearchParams>;
        
        // Initialize agent (injects submitResults tool)
        this.initializeAgent();
    }
    
    /**
     * Set the deck registry for resolving indexes to deck IDs.
     * Called before execution with available presentations.
     */
    public setDeckRegistry(
        registry: Map<number, { id: string; title: string; slidesReady: number; totalSlides: number }>,
        currentdocumentId?: string
    ): void {
        this.deckRegistry = registry;
        this.currentdocumentId = currentdocumentId;
        
        // Also set on the search tool
        const simpleRegistry = new Map<number, string>();
        for (const [idx, info] of registry) {
            simpleRegistry.set(idx, info.id);
        }
        this.searchTool.setDeckRegistry(simpleRegistry, currentdocumentId);
    }
    
    /**
     * Generate system prompt for the Presentation Library Search agent.
     * Uses the standardized 5-section prompt structure.
     */
    generateSystemPrompt(resolvedContext?: ResolvedContextItem[]): string {
        return buildInPresentationSystemPrompt({
            resolvedContext,
            deckRegistry: this.deckRegistry,
            tools: this.toolCalls,
        });
    }

    /**
     * Include explicit scope constraints in the user message so documentIds/documentId
     * passed by the caller are consistently consumed by the LLM/tool flow.
     */
    protected buildInitialMessages(
        params: InPresentationSearchParams,
        resolvedContext?: ResolvedContextItem[]
    ): ChatMessage[] {
        const messages = super.buildInitialMessages(params, resolvedContext);

        const hasdocumentIds = Array.isArray(params.documentIds) && params.documentIds.length > 0;
        const hasdocumentId = params.documentId !== undefined && params.documentId !== null;

        if (!hasdocumentIds && !hasdocumentId) {
            return messages;
        }

        const scopeDirective = hasdocumentIds
            ? `Search scope constraint: use documentIds exactly as provided (${params.documentIds!.map(String).join(', ')}).`
            : `Search scope constraint: use documentId exactly as provided (${String(params.documentId)}).`;

        if (messages.length > 0 && messages[0].role === 'user' && typeof messages[0].content === 'string') {
            messages[0] = {
                ...messages[0],
                content: `${messages[0].content}\n\n${scopeDirective}`,
            };
            return messages;
        }

        return [
            ...messages,
            {
                role: 'user' as const,
                content: scopeDirective,
            },
        ];
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // TOOL RESULT HANDLING
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Handle tool results - called via callback during tool loop.
     */
    private handleToolResult(toolName: string, result: unknown): void {
        if (toolName === 'searchPresentation' && result) {
            this.lastSearchResponse = result as SearchPresentationResponse;
            
            console.log(`[PresentationLibrarySearchAgent] Search complete: ${this.lastSearchResponse.count} results`);
            
            // Store results in GlobalStateManager
            this.storeResults(this.lastSearchResponse.results);
            
            // Record results for frontend-canonical conversation dedupe.
            if (this.currentConversationId) {
                GlobalStateManager.getInstance().recordPresentationLibrarySearchResults(
                    this.currentConversationId,
                    this.lastSearchResponse.results
                );
            }
            
            console.log(`[PresentationLibrarySearchAgent] Stored ${this.lastSearchResponse.results.length} results`);
        }
    }

    /**
     * Generate composite ID for storage.
     * - Components are globally unique: "component_abc12345"
     * - Slides need deck context: "deck_abc123|slide_257"
     * - Shapes need full context: "deck_abc123|slide_257|shape_42"
     */
    private generateContextId(result: SearchResult): string {
        const { resultType, resultId, presentationId, slideId, shapeId } = result;
        
        // Components are globally unique (UUID-based)
        if (resultType === 'component') {
            return resultId;
        }
        
        // Layouts are presentation-scoped
        if (resultType === 'layout') {
            return createCompositeId(presentationId, resultId);
        }
        
        // Shapes need full context
        if (resultType === 'shape' && slideId) {
            return createCompositeId(presentationId, slideId, shapeId || resultId);
        }
        
        // Slides need presentation context
        if (resultType === 'slide') {
            return createCompositeId(presentationId, resultId);
        }
        
        // Assets - use presentation + result ID
        return createCompositeId(presentationId, resultId);
    }

    /**
     * Store search results in GlobalStateManager for later retrieval
     */
    private storeResults(results: SearchResult[]): void {
        for (const result of results) {
            const contextId = this.generateContextId(result);
            
            const storedData: StoredResultData = {
                id: contextId,
                resultType: result.resultType,
                resultId: result.resultId,
                presentationId: result.presentationId,
                slideId: result.slideId,
                slideIndex: result.slideIndex,
                score: result.score,
                description: result.description,
                text: result.text,
                // Don't store large images in memory - retrieve on demand from backend
                // imageBase64: result.imageBase64,
                layoutId: result.layoutId,
                layoutName: result.layoutName,
                shapeId: result.shapeId,
                shapeType: result.shapeType,
                metadata: result.metadata,
                slideml: result.slideml,
                createdAt: new Date().toISOString(),
            };
            
            // Store using the composite ID
            GlobalStateManager.getInstance().addOrUpdateContext(contextId, storedData);
        }
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // HOOKS - Override base class hooks
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Prepare for execution - resolve current deck ID and reset tracking
     */
    protected override async beforeExecute(context: {
        params: InPresentationSearchParams;
        threadId: ThreadId;
        taskId: string;
        cancellationToken: CancellationToken;
        resolvedContext: ResolvedContextItem[];
        conversationId: string;
        documentId: string;
        callbacks?: AgentCallbacks;
    }): Promise<void> {
        console.log(`[PresentationLibrarySearchAgent] Starting execution with ${context.params.requirements.length} requirements`);
        this.lastSearchResponse = null;
        
        // Get current presentation context if not already set
        if (!this.currentdocumentId) {
            try {
                const presentationContext = await getCurrentPresentationContext();
                if (presentationContext) {
                    this.currentdocumentId = presentationContext.documentId;
                    console.log(`[PresentationLibrarySearchAgent] Current deck: ${this.currentdocumentId}`);
                }
            } catch (error) {
                console.warn('[PresentationLibrarySearchAgent] Could not get current presentation context:', error);
            }
        }
        
        // Update search tool with current deck ID
        if (this.currentdocumentId) {
            const simpleRegistry = new Map<number, string>();
            for (const [idx, info] of this.deckRegistry) {
                simpleRegistry.set(idx, info.id);
            }
            this.searchTool.setDeckRegistry(simpleRegistry, this.currentdocumentId);
        }
    }
    
    /**
     * Capture tool results - specifically search responses
     */
    protected onToolResult(toolName: string, result: unknown): void {
        this.handleToolResult(toolName, result);
    }

    /**
     * Inject persisted exclusion IDs for excludeAllPrevious before searchPresentation executes.
     */
    protected override async beforeToolExecute(context: ToolPreflightContext): Promise<ToolPreflightResult> {
        if (context.toolCall.name !== "searchPresentation") {
            return { action: "continue" };
        }

        if (!context.args || typeof context.args !== "object") {
            return { action: "continue" };
        }

        const args = context.args as Record<string, unknown>;
        if (args.excludeAllPrevious !== true) {
            return { action: "continue" };
        }

        const conversationId = this.currentConversationId;
        if (!conversationId) {
            return { action: "continue" };
        }

        const searchHistory = GlobalStateManager.getInstance().getConversationSearchHistory(conversationId);
        const exclusions = getPresentationLibraryExclusions(searchHistory);

        const mergeIds = (existing: unknown, additional: string[]): string[] => {
            const current = Array.isArray(existing) ? existing.map(String) : [];
            return [...new Set([...current, ...additional])];
        };

        args.slidesToExclude = mergeIds(args.slidesToExclude, exclusions.slidesToExclude);
        args.componentsToExclude = mergeIds(args.componentsToExclude, exclusions.componentsToExclude);
        args.assetsToExclude = mergeIds(args.assetsToExclude, exclusions.assetsToExclude);
        args.shapesToExclude = mergeIds(args.shapesToExclude, exclusions.shapesToExclude);
        args.layoutsToExclude = mergeIds(args.layoutsToExclude, exclusions.layoutsToExclude);

        console.log(
            `[PresentationLibrarySearchAgent] Injected excludeAllPrevious history: ` +
            `${(args.slidesToExclude as string[]).length} slides, ` +
            `${(args.componentsToExclude as string[]).length} components, ` +
            `${(args.assetsToExclude as string[]).length} assets, ` +
            `${(args.shapesToExclude as string[]).length} shapes, ` +
            `${(args.layoutsToExclude as string[]).length} layouts`
        );

        return { action: "continue" };
    }
    
    /**
     * Build final result with context ID note for orchestrator
     */
    protected buildFinalResult(
        results: ResultItem<PresentationLibraryResultData>[],
        threadId: ThreadId,
        tokensUsed?: number,
        summary?: string
    ): AgentResult<PresentationLibraryResultData> {
        if (results.length === 0) {
            return this.buildPartialResult(results, threadId, tokensUsed, summary);
        }
        
        const result = this.buildResult(results, threadId, tokensUsed, summary);
        result._contextIdNote = "Use the 'contextId' field from search results when calling submitResults (e.g., 'd0|slide_257', 'component_abc123'). These contextId values can also be passed to other agents in the 'context' parameter. The 'd0|' prefix references deck 0 in the registry, allowing token-efficient slide references.";
        return result;
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // RESULT PROCESSING (Submit Results Pattern)
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Process results from the submitResults tool.
     * This is the primary path when using the Submit Results Pattern.
     * 
     * Resolves result IDs to full context.
     */
    protected override async processSubmittedResults(
        submittedResults: SubmitResultsParams,
        _params: InPresentationSearchParams,
        context: {
            params: InPresentationSearchParams;
            threadId: ThreadId;
            taskId: string;
            cancellationToken: CancellationToken;
            resolvedContext: ResolvedContextItem[];
            conversationId: string;
            documentId: string;
            callbacks?: AgentCallbacks;
        }
    ): Promise<ResultItem<PresentationLibraryResultData>[]> {
        const results: ResultItem<PresentationLibraryResultData>[] = [];
        
        for (const submitted of submittedResults.results) {
            // Check cancellation
            context.cancellationToken.throwIfCancelled();
            
            // Extract result IDs from the submitted relevantResults
            const searchResultsField = submitted.relevantResults?.searchResults;
            const resultIds: string[] = Array.isArray(searchResultsField) 
                ? searchResultsField as string[]
                : [];
            
            // Resolve the IDs to full context
            const resolvedSearchResults = this.resolveSearchResults(resultIds);
            
            results.push({
                requirementId: submitted.requirementId,
                relevantResults: {
                    searchResults: resolvedSearchResults,
                },
                rationale: submitted.rationale,
            });
            
            console.log(`[PresentationLibrarySearchAgent] Requirement ${submitted.requirementId}: ${resolvedSearchResults.length} resolved results from submitResults`);
        }
        
        return results;
    }
    
    /**
     * Legacy: Process the LLM response and extract structured results.
     * This is a fallback for when submitResults is not called.
     */
    protected async processResults(
        responseContent: string,
        params: InPresentationSearchParams,
        context: {
            params: InPresentationSearchParams;
            threadId: ThreadId;
            taskId: string;
            cancellationToken: CancellationToken;
            resolvedContext: ResolvedContextItem[];
            conversationId: string;
            documentId: string;
            callbacks?: AgentCallbacks;
        }
    ): Promise<ResultItem<PresentationLibraryResultData>[]> {
        console.warn('[PresentationLibrarySearchAgent] processResults called - LLM did not use submitResults tool');
        
        const results: ResultItem<PresentationLibraryResultData>[] = [];
        const requirements = params.requirements;
        
        // Get all result IDs from the last search response
        const allResultIds = this.lastSearchResponse?.results.map(r => 
            this.generateContextId(r)
        ) || [];
        
        // For each requirement, extract relevant findings from the response
        for (const req of requirements) {
            // Check cancellation
            context.cancellationToken.throwIfCancelled();
            
            // Extract result IDs mentioned in the LLM response for this requirement
            const referencedIds = this.extractResultIdsFromResponse(responseContent, allResultIds);
            
            // If LLM didn't reference specific results, use all results
            const relevantIds = referencedIds.length > 0 ? referencedIds : allResultIds;
            
            // Build resolved search results with full content
            const resolvedResults = this.resolveSearchResults(relevantIds);
            
            // Build result for this requirement
            results.push({
                requirementId: req.id,
                relevantResults: {
                    searchResults: resolvedResults,
                },
                rationale: this.extractRationale(responseContent, req.requirement),
            });
            
            console.log(`[PresentationLibrarySearchAgent] Requirement ${req.id}: ${resolvedResults.length} resolved results (fallback path)`);
        }
        
        return results;
    }
    
    /**
     * Resolve result IDs to full context for the orchestrator
     */
    private resolveSearchResults(resultIds: string[]): ResolvedPresentationSearchResultItem[] {
        const resolved: ResolvedPresentationSearchResultItem[] = [];
        
        for (const resultId of resultIds) {
            // Try to find in GlobalStateManager first
            const resolvedContent = GlobalStateManager.getInstance().getContext(resultId);
            const stored = resolvedContent?.content as StoredResultData | null;
            
            if (stored) {
                resolved.push({
                    contextId: stored.id,
                    resultType: stored.resultType,
                    resultId: stored.resultId,
                    presentationId: stored.presentationId,
                    slideId: stored.slideId,
                    slideIndex: stored.slideIndex,
                    score: stored.score,
                    description: stored.description,
                    text: stored.text,
                    slideml: stored.slideml,
                });
            } else {
                // Check if it's a simple ID that might be in the last search response
                const fromSearch = this.lastSearchResponse?.results.find(r => 
                    r.resultId === resultId || 
                    this.generateContextId(r) === resultId
                );
                
                if (fromSearch) {
                    resolved.push({
                        contextId: this.generateContextId(fromSearch),
                        resultType: fromSearch.resultType,
                        resultId: fromSearch.resultId,
                        presentationId: fromSearch.presentationId,
                        slideId: fromSearch.slideId,
                        slideIndex: fromSearch.slideIndex,
                        score: fromSearch.score,
                        description: fromSearch.description,
                        text: fromSearch.text,
                        slideml: fromSearch.slideml,
                    });
                } else {
                    console.warn(`[PresentationLibrarySearchAgent] Could not resolve result: ${resultId}`);
                }
            }
        }
        
        return resolved;
    }
    
    /**
     * Extract result IDs mentioned in the LLM response
     */
    private extractResultIdsFromResponse(response: string, validIds: string[]): string[] {
        const mentioned: string[] = [];
        
        // Look for common ID patterns in the response
        const patterns = [
            /slide_[a-z0-9_]+/gi,      // slide_257
            /component_[a-z0-9]+/gi,   // component_abc123
            /layout_[a-z0-9_]+/gi,     // layout_title
            /shape_[a-z0-9_]+/gi,      // shape_42
            /asset_[a-z0-9_]+/gi,      // asset_xyz
        ];
        
        for (const pattern of patterns) {
            const matches = response.match(pattern) || [];
            for (const match of matches) {
                // Check if it's in our valid IDs or a substring match
                const normalizedMatch = match.toLowerCase();
                for (const validId of validIds) {
                    if (validId.toLowerCase().includes(normalizedMatch) || 
                        normalizedMatch.includes(validId.toLowerCase())) {
                        if (!mentioned.includes(validId)) {
                            mentioned.push(validId);
                        }
                        break;
                    }
                }
            }
        }
        
        return mentioned;
    }
    
    /**
     * Extract rationale from response for a specific requirement
     */
    private extractRationale(response: string, requirement: string): string {
        if (response && response.trim().length > 0) {
            return response.trim();
        }
        
        return `Searched presentation for: ${requirement}`;
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export default PresentationLibrarySearchAgent;




